import React from "react"
import {
  Flow,
  type MoneyFlow,
  type TransactionSummary,
} from "@digits-graphql/frontend/graphql-bearer"
import { CopyableAmount } from "@digits-shared/DesignSystem/CopyableAmount"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import numberHelper, { CurrencyStyle } from "@digits-shared/helpers/numberHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { type ExportOptions } from "src/frontend/components/Shared/Transactions/Categories/categoryTableTypes"
import { ExportOptionsButton } from "src/frontend/components/Shared/Transactions/TableCells/ExportOptionsButton"
import { FlowAmount } from "src/frontend/components/Shared/Transactions/TableCells/FlowAmount"

/*
  STYLES
*/

const Total = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 14px;
  font-weight: ${fonts.weight.heavy};
`

const TransactionCount = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: ${fonts.weight.medium};
  text-align: right;
  color: ${colors.secondary50};
`

const DetailsGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`

/*
  INTERFACES
*/

interface SummaryProps {
  summary: TransactionSummary | undefined
  exportOptions?: ExportOptions
  useColors?: boolean
}

/*
  COMPONENTS
*/

export const TableSummary: React.FC<SummaryProps> = ({
  summary,
  exportOptions,
  useColors = true,
}) => {
  const moneyFlow = summary?.total.moneyFlow ?? moneyFlowHelper.buildZeroMoneyFlow()
  const transactionsCount = summary?.total.transactionsCount ?? 0

  return (
    <DetailsGroup>
      <Total>
        <SummaryTotal
          className={stringHelper.classNames(!useColors && "no-colors")}
          moneyFlow={moneyFlow}
        />
      </Total>
      <TransactionCount>
        {exportOptions && !!transactionsCount && <ExportOptionsButton {...exportOptions} />}
        {`${transactionsCount} ${stringHelper.pluralize(transactionsCount, "Transaction", "Transactions")}`}
      </TransactionCount>
    </DetailsGroup>
  )
}

const SummaryTotal: React.FC<{ className?: string; moneyFlow: MoneyFlow }> = ({
  className,
  moneyFlow,
}) => {
  const { isNormal, businessFlow, value } = moneyFlow
  const isZero = value.amount === 0

  const classNames = stringHelper.classNames(
    className,
    "heading",
    businessFlow === Flow.Inbound ? "inbound" : "outbound",
    isNormal || isZero ? "normal" : "abnormal",
    !isNormal && !isZero && "negative"
  )
  return (
    <CopyableAmount amount={moneyFlow}>
      <FlowAmount className={classNames}>
        {numberHelper.currency(value, { style: CurrencyStyle.Detail, absolute: true })}
      </FlowAmount>
    </CopyableAmount>
  )
}
